/**
 * <gh-gallery-progress> — 2px progress bar under the product gallery on phones (Gaucho House).
 *
 * Lives inside Horizon's <media-gallery> (snippets/gh-gallery-progress.liquid) and follows the
 * scroll of its slideshow (<slideshow-slides>, a native scroll-snap scroller). The bar is one
 * slide's share of the track and slides along it, like the home mobile carousels.
 */
class GhGalleryProgress extends HTMLElement {
  connectedCallback() {
    this.gallery = this.closest('media-gallery');
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };

    this.attach();
    // Horizon morphs the gallery when the variant changes: re-attach to the new scroller
    this.observer = new MutationObserver(() => this.attach());
    if (this.gallery) this.observer.observe(this.gallery, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  attach() {
    const scroller = this.gallery?.querySelector('slideshow-slides');
    if (!scroller || scroller === this.scroller) return;

    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();

    this.scroller = scroller;
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.scroller);
    this.update();
  }

  update() {
    if (!this.scroller) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
    const max = scrollWidth - clientWidth;
    const count = Math.max(this.scroller.children.length, 1);
    const ratio = max > 2 ? Math.min(Math.max(Math.abs(scrollLeft) / max, 0), 1) : 0;

    this.style.setProperty('--gh-progress-count', String(count));
    this.style.setProperty('--gh-progress', ratio.toFixed(4));
  }
}

if (!customElements.get('gh-gallery-progress')) customElements.define('gh-gallery-progress', GhGalleryProgress);
